/**
 * STATUS — WHAT THIS DEPLOYMENT OF SCOUT IS, AND WHAT IT CANNOT DO HERE.
 *
 * This is the CLI's `status` command's web replacement (parity row 3), and it
 * is also the first real thing the workspace does: it reads
 * `GET /gateway/capabilities` through the product's own gateway, over the real
 * session, and renders the disclosure block the backend puts at the head of
 * every payload.
 *
 * THREE RULES SHAPE EVERY LINE OF IT, AND THEY COME FROM THE BRIEF RATHER THAN
 * FROM TASTE:
 *
 *   1. NO COUNT IS PRESENTED AS PROGRESS. "29 capabilities" is a count of
 *      declarations in this build. It is labelled as one. A rising number here
 *      would mean a developer added a declaration, not that Scout learned
 *      anything.
 *   2. UNKNOWN AND UNREACHABLE ARE DIFFERENT WORDS. `AVAILABLE_BUT_NO_RUNTIME_PRODUCER`
 *      is "nothing in this build produces it"; `DECLARED_NOT_REACHABLE_HERE` is
 *      "this deployment was never given the wiring". The registry's own
 *      vocabulary is rendered rather than paraphrased, because paraphrasing it
 *      is how two different facts start looking the same.
 *   3. THE FOURTH RUNG IS SHOWN WHEN IT IS KNOWN. A capability can be
 *      `AVAILABLE_AND_REACHABLE` and never have run here. Where the deployment
 *      reported how far it actually got, that is stated separately; where it
 *      was silent, the silence is not turned into a claim.
 *
 * WHAT IS DELIBERATELY NOT ON THIS SCREEN: the resource account. It is in the
 * payload this view reads, and it belongs to Resources (slice 8), where it can
 * be shown with the run-scoped accuracy it needs rather than as a number lifted
 * onto a status page.
 */

import { useEffect, useMemo } from 'preact/hooks';
import { createLoadable } from '../resource';
import { readGateway } from '../gateway';
// THE FAILURE RENDERER IS SHARED NOW, because four more views need exactly this
// and four copies is four chances for the 403 to drift into a generic "something
// went wrong" — which tells the Owner to try again when trying again cannot work.
import { Failure } from './failure';
import { PageHeader } from './about';
import { HealthPanel } from './health';

/** The disclosure block the gateway puts at the head of every payload. */
interface Disclosure {
	version: string;
	generatedAt: string;
	faculties: {
		search: { available: string[]; unavailable: string[] };
		reasoning: { available: boolean };
	};
	authority: string;
}

interface Capability {
	key: string;
	label: string;
	status: string;
	producedBy: string | null;
	reason: string | null;
	runtimeLevel?: string;
}

interface Capabilities extends Disclosure {
	searchDescription: string;
	cognition: Capability[];
	resourceAccount: unknown;
}

/** THE REGISTRY'S OWN WORDS, RENDERED AS ITS OWN WORDS. Each caption is a
 *  restatement of the constant's docstring in `core/reachability.ts`, and none
 *  of them is a quality judgement — a capability that cannot be reached here is
 *  a deployment fact, not a defect in Scout. */
const STATUS_CAPTIONS: Record<string, string> = {
	AVAILABLE_AND_REACHABLE:
		'the machinery exists in this build and a runtime path here produces its records',
	AVAILABLE_BUT_NO_RUNTIME_PRODUCER:
		'the machinery exists in this build and nothing in it produces these records',
	DECLARED_NOT_REACHABLE_HERE:
		'a runtime path is declared, and this deployment was never given the binding, credential or host it needs',
	CONFIGURED_NOT_VERIFIED_HERE:
		'the wiring is present here and nothing has yet demonstrated that it works',
};

const RUNTIME_LEVEL_CAPTIONS: Record<string, string> = {
	NOT_CONFIGURED: 'this deployment has none of the wiring this needs',
	CONFIGURED: 'the wiring is present; nothing has verified it',
	REACHABLE: 'something verified the path — evidence, not intent',
	EXERCISED: 'it has produced a record here',
};

function Count({ value }: { value: number }) {
	return <span class="count">{value}</span>;
}

export function StatusView() {
	// THE LOADABLE IS CREATED ONCE, AND THAT IS LOAD-BEARING RATHER THAN
	// TIDY. Built inline it would be a NEW signal on every render: the effect
	// would fill the first one, the component would re-render reading the
	// second, and the view would say "reading…" forever while the answer it
	// asked for sat in a signal nobody was subscribed to. The render specimen
	// caught exactly that, which is the reason a shell gets rendered in a test
	// at all.
	const capabilities = useMemo(
		() =>
			createLoadable<Capabilities>(() =>
				readGateway<Capabilities>('/api/scout/gateway/capabilities'),
			),
		[],
	);

	useEffect(() => {
		void capabilities.load();
	}, [capabilities]);

	const state = capabilities.state.value;
	const data = state.data;

	// WHAT THIS VIEW CANNOT ESTABLISH STAYS IN THE OPEN. The About panel is for
	// orientation; a limitation a reader could mistake for a clean bill of
	// health is a warning, and a warning behind a button is a warning the Owner
	// has to already suspect to find.
	const limits = (
		<>
			<p>
				<strong>A capability nobody built does not appear on this list.</strong> The registry can
				only report on what was declared, so absent is not the same as empty.
			</p>
			<p>
				<strong>Declared and reachable is not the same as working.</strong> It means no declared
				capability lacks a producer in this build; it says nothing about whether the code behind
				one is correct.
			</p>
			<p>
				<strong>This is a read of one deployment, taken once.</strong> It does not refresh itself.
			</p>
		</>
	);

	return (
		<section class="view" aria-labelledby="status-heading">
			<PageHeader
				id="status-heading"
				title="Status"
				about={{
					purpose:
						'What this deployment of Scout is, what it can reach, and what it cannot — read live from Scout’s own gateway.',
				}}
			/>

			{state.status === 'loading' && (
				<p class="notice" role="status">
					Reading Scout’s capability registry…
				</p>
			)}

			{state.status === 'failed' && state.error !== null && (
				<Failure
						error={state.error}
						what="what this deployment is"
						onRetry={() => void capabilities.load()}
					/>
			)}

			{state.status === 'ready' && data !== null && (
				<>
					<section class="panel" aria-labelledby="deployment-heading">
						<h2 id="deployment-heading">This deployment</h2>
						<dl class="facts">
							<div>
								<dt>Gateway</dt>
								<dd>
									<code>{data.version}</code>
								</dd>
							</div>
							<div>
								<dt>Answered at</dt>
								<dd>
									<time dateTime={data.generatedAt}>{data.generatedAt}</time>
								</dd>
							</div>
							<div>
								<dt>Search faculty</dt>
								<dd>
									{data.faculties.search.available.length > 0 ? (
										<>
											{data.faculties.search.available.join(', ')}
											{data.faculties.search.unavailable.length > 0 && (
												<span class="muted">
													{' '}
													— not configured here:{' '}
													{data.faculties.search.unavailable.join(', ')}
												</span>
											)}
										</>
									) : (
										<span class="muted">
											none configured here
											{data.faculties.search.unavailable.length > 0 &&
												` (${data.faculties.search.unavailable.join(', ')} declared, no credential)`}
										</span>
									)}
								</dd>
							</div>
							<div>
								<dt>Reasoning faculty</dt>
								<dd>
									{data.faculties.reasoning.available
										? 'available to this deployment'
										: 'no reasoning credential is configured here, so every model-mediated path refuses by name'}
								</dd>
							</div>
						</dl>
						<p class="authority">{data.authority}</p>
						<p class="muted small">
							Scout does not disclose which model or provider runs it. That is a product
							boundary, not an omission, and it is why no model name appears anywhere on this
							screen.
						</p>
					</section>

					<section class="panel" aria-labelledby="capabilities-heading">
						<h2 id="capabilities-heading">
							Capabilities <Count value={data.cognition.length} />
						</h2>
						<p class="muted small">
							A count of what this build <em>declares</em>, not of what Scout has achieved.
							It rising would mean a declaration was added. A capability nobody built does
							not appear on this list: absent is not the same as empty, and this screen
							cannot show you what Scout cannot see.
						</p>
						{/* THE FOUR STATUSES, COUNTED SEPARATELY, BECAUSE THE POINT OF THE
						    VOCABULARY IS THAT THEY ARE DIFFERENT FACTS. A single
						    "reachable" figure would put "nothing in this build produces
						    it" and "this deployment was never given the binding" in one
						    bucket, which is the confusion the registry exists to prevent. */}
						<ul class="status-summary">
							{Object.entries(STATUS_CAPTIONS).map(([status, caption]) => {
								const matching = data.cognition.filter(
									(capability) => capability.status === status,
								).length;
								return (
									<li key={status}>
										<span class="count">{matching}</span> <code>{status}</code>{' '}
										<span class="muted">— {caption}</span>
									</li>
								);
							})}
						</ul>
						<ul class="capabilities">
							{data.cognition.map((capability) => (
								<li key={capability.key} class="capability">
									<div class="capability-head">
										<span class="capability-label">{capability.label}</span>
										<code class="capability-key">{capability.key}</code>
									</div>
									<p class="capability-status">
										<code>{capability.status}</code>{' '}
										<span class="muted">
											— {STATUS_CAPTIONS[capability.status] ?? 'this build does not describe this status'}
										</span>
									</p>
									{capability.runtimeLevel !== undefined && (
										<p class="capability-runtime muted small">
											How far this deployment got: <code>{capability.runtimeLevel}</code> —{' '}
											{RUNTIME_LEVEL_CAPTIONS[capability.runtimeLevel] ??
												'this build does not describe this rung'}
										</p>
									)}
									{capability.producedBy !== null && (
										<p class="capability-producer muted small">
											Produced by <code>{capability.producedBy}</code>
										</p>
									)}
									{capability.reason !== null && (
										<p class="capability-reason small">{capability.reason}</p>
									)}
								</li>
							))}
						</ul>
					</section>

					</>
				)}
				<HealthPanel />
				<section class="panel" aria-labelledby="status-limits">
					<h2 id="status-limits">What is not known here</h2>
					{limits}
				</section>
			</section>
	);
}
